// src/components/sourcex/SourceXTour.jsx
//
// The SourceX built-documents guided tour (2026-10-29). Steps come from
// sourcexTourSteps.js. For each step the page is made ready first (`onPrepare`,
// view only), then the element marked data-tour="<target>" is scrolled smoothly into
// view and spotlighted, with a short card next to it. When it isn't on screen, the
// card shows in the middle with the step's `fallback` (where to find it).
//
// Smooth by design: the dim layer never changes, the spotlight glides from one
// element to the next, and the card fades out, moves and fades back in once the
// page has stopped scrolling. Reduced-motion settings get no animation.
//
// Explains only: the layer catches every click, so nothing on the page can be
// pressed by accident while the tour runs. Portalled to <body>, above every pop-up
// on the page (z 110); its own sx-tour-* classes are set in both light and dark mode.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

const PAD = 6; // space between the element and the spotlight ring
const CARD_W = 340;
const GAP = 12; // space between the spotlight and the card

const isVisible = (el) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

const reducedMotion = () => {
  try {
    return !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/** The first visible element for a step (the approve button can be in two banners). */
export const findTourTarget = (target, root = document) => {
  if (!target) return null;
  const all = [...root.querySelectorAll(`[data-tour="${target}"]`)];
  return all.find(isVisible) || null;
};

// Resolves once the element has stopped moving (a smooth scroll has finished), or
// after `maxMs` at the latest.
const settle = async (el, maxMs = 900) => {
  let last = null;
  let still = 0;
  for (let t = 0; t < maxMs; t += 50) {
    const r = el.getBoundingClientRect();
    const same = last && Math.abs(r.top - last.top) < 0.5 && Math.abs(r.left - last.left) < 0.5;
    still = same ? still + 1 : 0;
    if (still >= 2) return;
    last = r;
    // eslint-disable-next-line no-await-in-loop
    await wait(50);
  }
};

// Where the card goes: below the spotlight if it fits, else above, else beside it;
// always inside the window. With no spotlight: the middle of the window.
const cardPosition = (rect, cardH) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) return { top: Math.max(8, (vh - cardH) / 2), left: Math.max(8, (vw - CARD_W) / 2) };
  const left = Math.min(Math.max(8, rect.left), Math.max(8, vw - CARD_W - 8));
  if (rect.bottom + PAD + GAP + cardH <= vh) return { top: rect.bottom + PAD + GAP, left };
  if (rect.top - PAD - GAP - cardH >= 0) return { top: rect.top - PAD - GAP - cardH, left };
  return {
    top: Math.max(8, Math.min(vh - cardH - 8, rect.top)),
    left: Math.max(8, Math.min(vw - CARD_W - 8, rect.right + GAP)),
  };
};

/**
 * @param {object}   props
 * @param {object[]} props.steps       from sourcexTourSteps.js
 * @param {(needs?: string) => (void|Promise)} [props.onPrepare] make the page ready for a step (view only)
 * @param {() => void} props.onClose    the tour ended (finished or stopped)
 */
export default function SourceXTour({ steps, onPrepare, onClose }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [shown, setShown] = useState(false); // card visible (faded in) for this step
  // The step the card is SHOWING: it keeps the old words while fading out and
  // switches only once it has moved, so text never changes in mid-air.
  const [shownIndex, setShownIndex] = useState(0);
  const [cardH, setCardH] = useState(180);
  const cardRef = useRef(null);
  const nextRef = useRef(null);
  const targetRef = useRef(null);
  const frame = useRef(0);

  const step = steps[index] || null;
  const shownStep = steps[shownIndex] || step;
  const last = shownIndex === steps.length - 1;

  const measure = useCallback(() => {
    const el = targetRef.current;
    setRect(el && el.isConnected && isVisible(el) ? el.getBoundingClientRect() : null);
  }, []);

  // Each step: fade the card out, get the page ready, find the element, scroll it
  // into view, wait for the scroll to finish, then move there and fade back in.
  useEffect(() => {
    if (!step) return undefined;
    let cancelled = false;
    setShown(false);
    (async () => {
      try {
        if (onPrepare) await onPrepare(step.needs);
      } catch {
        // A step that can't be prepared still shows, with its fallback text.
      }
      await wait(80); // let the page draw what onPrepare opened
      if (cancelled) return;
      const el = findTourTarget(step.target);
      targetRef.current = el;
      if (el) {
        if (typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ block: "center", inline: "nearest", behavior: reducedMotion() ? "auto" : "smooth" });
        }
        await settle(el);
        if (cancelled) return;
      }
      measure();
      setShownIndex(steps.indexOf(step));
      setShown(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [step, steps, onPrepare, measure]);

  // Keep the spotlight on the element while the page scrolls or resizes (once a frame).
  useEffect(() => {
    const onMove = () => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        measure();
      });
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [measure]);

  useLayoutEffect(() => {
    if (cardRef.current && cardRef.current.offsetHeight) setCardH(cardRef.current.offsetHeight);
    if (shown && nextRef.current) nextRef.current.focus({ preventScroll: true });
  }, [shown, shownIndex]);

  // Next / Back act only once the current step is fully shown: quick repeated clicks
  // used to run ahead through steps nobody saw, and could close the tour.
  const indexRef = useRef(0);
  const shownRef = useRef(false);
  indexRef.current = index;
  shownRef.current = shown;
  const go = useCallback(
    (delta) => {
      if (!shownRef.current) return;
      const n = indexRef.current + delta;
      if (n < 0) return;
      if (n >= steps.length) {
        onClose();
        return;
      }
      setIndex(n);
    },
    [steps.length, onClose]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  if (!step) return null;
  // `rect` changes only once the next element is found and still, so while the card
  // fades out the spotlight and card stay where they are, then glide to the next.
  const spotlit = !!rect;
  const pos = cardPosition(spotlit ? rect : null, cardH);
  const found = spotlit;
  const text = found || !shownStep.fallback ? shownStep.body : `${shownStep.body} ${shownStep.fallback}`;
  // No element: the spotlight shrinks to a point in the middle, so the dim stays the
  // same and the next element's spotlight grows out of it.
  const spot = spotlit
    ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
    : { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 };

  return createPortal(
    <div className="sx-tour-root fixed inset-0 z-[110]" data-testid="sourcex-tour">
      {/* Catches every click: nothing on the page can be pressed during the tour. */}
      <div className="absolute inset-0" aria-hidden="true" />
      <div
        className={`sx-tour-spot pointer-events-none fixed rounded-xl ${spotlit ? "" : "sx-tour-spot-none"}`}
        data-testid="sourcex-tour-spotlight"
        data-found={spotlit ? "true" : "false"}
        style={spot}
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label="SourceX tour"
        aria-busy={!shown}
        className={`sx-tour-card fixed rounded-2xl border p-4 shadow-2xl ${shown ? "sx-tour-card-in" : "sx-tour-card-out"}`}
        style={{ top: pos.top, left: pos.left, width: CARD_W }}
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="sx-tour-title text-sm font-semibold">{shownStep.title}</h3>
          <button type="button" onClick={onClose} aria-label="End tour" className="sx-tour-close -mr-1 -mt-1 rounded-lg p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="sx-tour-body mt-1.5 text-[13px] leading-relaxed">{text}</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="sx-tour-count text-[11px]">
            {shownIndex + 1} of {steps.length}
          </span>
          <div className="flex items-center gap-2">
            {shownIndex > 0 && (
              <button type="button" onClick={() => go(-1)} className="sx-tour-back inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[12px]">
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            )}
            <button
              ref={nextRef}
              type="button"
              onClick={() => go(1)}
              className="sx-tour-next inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold"
            >
              {last ? "Done" : "Next"}
              {!last && <ArrowRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
